import { useAuth } from "@workos-inc/authkit-react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { io } from "socket.io-client";
import { toast } from "sonner";
import { useCacheMutations } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import { logger } from "@/shared/utils/logger";
import { TOAST_DURATION } from "@/shared/utils/toast";

export type { Entry };

// ── Real-time entry-lifecycle events (names must match backend `src/socket.ts`) ──
// Delivery is scoped to the user's private Socket.IO room, so payloads no longer
// carry an email and the client does not need to filter by one.
type EntryReceivedEvent = {
	entryId: string;
	application?: string | null;
	date?: string | null;
	mode: "create" | "update";
	documentName?: string | null;
};
type EntryMatterPredictedEvent = { entryId: string; matter?: string | null };
type EntryTerminalEvent = {
	entryId: string;
	application?: string | null;
	reason?: string | null;
};
// Legacy admin-feedback event is still a broadcast carrying an email.
type EntryFeedbackEvent = { email?: string };

/**
 * A processing entry the backend has told us about but that isn't in the cache
 * yet. `create` rows render as optimistic skeletons; `update` rows only feed the
 * processing indicator (the real row already exists and we can't correlate the
 * source id to a cache row).
 */
export type OptimisticEntryPhase =
	| "received"
	| "matter"
	| "finalizing"
	| "error";
export type OptimisticEntry = {
	entryId: string;
	application: string | null;
	documentName: string | null;
	mode: "create" | "update";
	phase: OptimisticEntryPhase;
	matter: string | null;
	startedAt: number;
};

// Drop any optimistic row we never got a terminal event for, so a missed
// completed/discarded/failed can't leave a skeleton spinning forever.
const OPTIMISTIC_TTL_MS = 60_000;

// How long the "Finalizing…" beat is held after generation completes before the
// real row swaps in, so the close reads as deliberate instead of a flash. Per
// entry via its own timer — never queued.
const OPTIMISTIC_FINALIZE_MS = 1300;

// Entries land in bursts, and each refresh invalidates the whole entry-data key
// family — the visible list, both calendar summaries, and any mounted reporting
// dashboard. Throttled rather than debounced so an isolated entry still lands
// immediately; only a burst pays the wait, collapsing into one trailing refetch.
const REFRESH_MIN_INTERVAL_MS = 1500;

function getSocketApiBaseUrl(hostname: string) {
	const subdomain = hostname.split(".")[0];

	if (hostname.includes("bpmlaw")) return "https://hourglassapi.bpmlaw.com";
	if (hostname.includes("tysonmendes")) {
		return "https://hourglassapi.tysonmendes.com";
	}
	if (hostname.includes("pregodonnell")) {
		return "https://hourglassapi.pregodonnell.com";
	}
	if (hostname.includes("localhost")) return "http://localhost:8000";
	if (hostname.includes("app.hourglass.law"))
		return "https://api.hourglass.law";

	return `https://${subdomain}api.hourglass.law`;
}

/**
 * The provider owns the entry-processing socket. Views read their own entry
 * rows through `useEntriesCache` with their own date scope — the provider
 * deliberately holds no entry data of its own.
 */
type EntriesContextType = {
	isLoading: boolean;
	/** Create-mode processing entries not yet in the cache — render as skeletons. */
	optimisticEntries: OptimisticEntry[];
	/** Coalesced refetch of the entry caches. Resolves when the refetch settles. */
	refreshRecords: () => Promise<void>;
};

const EntriesContext = createContext<EntriesContextType | undefined>(undefined);

export function EntriesProvider({ children }: { children: ReactNode }) {
	const { user, isLoading: isAuthLoading, getAccessToken } = useAuth();
	const { refresh, refreshAll } = useCacheMutations();
	const refreshAllRef = useRef(refreshAll);

	useEffect(() => {
		refreshAllRef.current = refreshAll;
	}, [refreshAll]);

	const [optimisticEntries, setOptimisticEntries] = useState<OptimisticEntry[]>(
		[],
	);
	const showedOfflineToast = useRef(false);

	// Mirror so the TTL-reconcile interval can read current rows without a stale
	// closure and without doing side effects inside a setState updater.
	const optimisticEntriesRef = useRef(optimisticEntries);

	useEffect(() => {
		optimisticEntriesRef.current = optimisticEntries;
	}, [optimisticEntries]);

	// Refs so the once-mounted WebSocket effect always reads current values.
	const getAccessTokenRef = useRef(getAccessToken);
	const userRef = useRef(user);

	useEffect(() => {
		getAccessTokenRef.current = getAccessToken;
		userRef.current = user;
	}, [getAccessToken, user]);

	// ── Optimistic-entry store helpers ──────────────────────────────────
	const upsertOptimistic = useCallback((entry: OptimisticEntry) => {
		setOptimisticEntries((prev) => {
			const next = prev.filter((e) => e.entryId !== entry.entryId);
			next.push(entry);
			return next;
		});
	}, []);

	const patchOptimistic = useCallback(
		(entryId: string, patch: Partial<OptimisticEntry>) => {
			setOptimisticEntries((prev) =>
				prev.map((e) => (e.entryId === entryId ? { ...e, ...patch } : e)),
			);
		},
		[],
	);

	const removeOptimistic = useCallback((entryId: string) => {
		setOptimisticEntries((prev) => prev.filter((e) => e.entryId !== entryId));
	}, []);

	// ── Coalesced cache refresh ─────────────────────────────────────────
	// One timer for the whole provider. An idle request runs straight away;
	// anything arriving inside the interval joins a single trailing run, so a
	// burst of completed entries costs one invalidation rather than one each.
	const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const refreshLastRunAt = useRef(0);
	const refreshInFlight = useRef(false);
	const refreshWaiters = useRef<Array<() => void>>([]);

	const runRefresh = useCallback(async () => {
		refreshTimer.current = null;
		refreshLastRunAt.current = Date.now();
		refreshInFlight.current = true;
		const waiters = refreshWaiters.current;
		refreshWaiters.current = [];
		try {
			const startTime = performance.now();
			await refresh("entries");
			logger.debug("Records refreshed", {
				coalescedCallers: waiters.length,
				durationMs: Math.round(performance.now() - startTime),
			});
		} catch (e) {
			if (e instanceof Error) {
				logger.captureException(e, { action: "refreshRecords" });
			}
			// Don't double-toast while the offline toast is already up.
			if (!showedOfflineToast.current) {
				toast.error("Failed to load entries", {
					id: "refresh-records-error",
					description: e instanceof Error ? e.message : undefined,
					duration: TOAST_DURATION.error,
				});
			}
		} finally {
			refreshInFlight.current = false;
			for (const resolve of waiters) resolve();
		}
	}, [refresh]);

	const runRefreshRef = useRef(runRefresh);

	useEffect(() => {
		runRefreshRef.current = runRefresh;
	}, [runRefresh]);

	const refreshRecords = useCallback((): Promise<void> => {
		const promise = new Promise<void>((resolve) => {
			refreshWaiters.current.push(resolve);
		});
		// A run is already queued — this caller joins it rather than adding one.
		if (refreshTimer.current) return promise;

		// Callers arriving mid-flight wait a full interval: the in-flight refetch
		// may have started before their entry was written, so it can't serve them.
		const sinceLastRun = Date.now() - refreshLastRunAt.current;
		const delay = refreshInFlight.current
			? REFRESH_MIN_INTERVAL_MS
			: Math.max(0, REFRESH_MIN_INTERVAL_MS - sinceLastRun);

		if (delay === 0) {
			void runRefreshRef.current();
			return promise;
		}
		refreshTimer.current = setTimeout(() => {
			void runRefreshRef.current();
		}, delay);
		return promise;
	}, []);

	const refreshRecordsRef = useRef(refreshRecords);

	useEffect(() => {
		refreshRecordsRef.current = refreshRecords;
	}, [refreshRecords]);

	// Drop the pending timer on unmount and release anyone still awaiting it,
	// so a caller's `.finally()` cleanup can't be stranded.
	useEffect(() => {
		return () => {
			if (refreshTimer.current) clearTimeout(refreshTimer.current);
			refreshTimer.current = null;
			const waiters = refreshWaiters.current;
			refreshWaiters.current = [];
			for (const resolve of waiters) resolve();
		};
	}, []);

	// Reconcile: drop optimistic rows that never received a terminal event, so a
	// dropped socket message can't leave a skeleton spinning. Check staleness
	// against a ref (not inside the state updater) so the refetch is a clean
	// side effect that runs once — not twice under React Strict Mode.
	useEffect(() => {
		const interval = setInterval(() => {
			const now = Date.now();
			const hasStale = optimisticEntriesRef.current.some(
				(e) => now - e.startedAt > OPTIMISTIC_TTL_MS,
			);
			if (!hasStale) return;
			setOptimisticEntries((prev) =>
				prev.filter((e) => now - e.startedAt <= OPTIMISTIC_TTL_MS),
			);
			void refreshRecordsRef.current();
		}, 10_000);
		return () => clearInterval(interval);
	}, []);

	// WebSocket connection for real-time entry processing updates
	useEffect(() => {
		const apiUrlBase = getSocketApiBaseUrl(window.location.hostname);
		const socket = io(apiUrlBase, {
			transports: ["websocket"], // server runs PM2 cluster without sticky sessions; polling is disabled server-side
			reconnectionAttempts: 30,
			reconnectionDelayMax: 15000,
			// Fetch a fresh WorkOS token for each (re)connect so the server can
			// authenticate the socket and place it in the user's private room.
			auth: (cb) => {
				getAccessTokenRef
					.current()
					.then((token) => cb({ token }))
					.catch(() => cb({ token: "" }));
			},
		});

		const loadRecords = async () => {
			try {
				await refreshRecordsRef.current();
			} catch (error) {
				if (error instanceof Error) {
					logger.captureException(error, { action: "loadRecordsWebSocket" });
				}
			}
		};

		// Track handler-scheduled timers so they're cancelled on effect cleanup
		// (provider unmount / Strict Mode teardown) — a stale timer must not fire
		// removeOptimistic/loadRecords against a torn-down effect.
		const timers = new Set<ReturnType<typeof setTimeout>>();
		const schedule = (fn: () => void, ms: number) => {
			const id = setTimeout(() => {
				timers.delete(id);
				fn();
			}, ms);
			timers.add(id);
		};

		socket.on("connect", () => {
			logger.debug("WebSocket connected");
			// Gate on the offline toast so sub-second blips don't trigger a refetch.
			if (showedOfflineToast.current) {
				showedOfflineToast.current = false;
				toast.dismiss("ws-offline");
				toast.success("Reconnected", { id: "ws-reconnected", duration: 2000 });
				// A completion may have been missed while offline — clear any pending
				// skeletons and refetch from scratch.
				setOptimisticEntries([]);
				refreshAllRef.current();
			}
		});

		// Backend accepted an activity chunk and is generating an entry.
		socket.on("entry_received", (data: EntryReceivedEvent) => {
			if (!data?.entryId) return;
			upsertOptimistic({
				entryId: data.entryId,
				application: data.application ?? null,
				documentName: data.documentName ?? null,
				mode: data.mode === "update" ? "update" : "create",
				phase: "received",
				matter: null,
				startedAt: Date.now(),
			});
		});

		// Progressive fill: the matter was predicted mid-pipeline.
		socket.on("entry_matter_predicted", (data: EntryMatterPredictedEvent) => {
			if (!data?.entryId) return;
			patchOptimistic(data.entryId, {
				matter: data.matter ?? null,
				phase: "matter",
				// Advance the TTL clock so a long narrative phase doesn't expire
				// mid-generation (the 60s TTL is anchored to `startedAt`).
				startedAt: Date.now(),
			});
		});

		// Entry written to the DB. Close on a "Finalizing…" beat, and hold the
		// swap until the skeleton has met its minimum visible time — then refetch
		// and drop it together (the row and the skeleton removal land as one, so
		// no flash and no duplicate).
		socket.on("entry_processing_completed", (data: EntryTerminalEvent) => {
			if (!data?.entryId) return;
			const { entryId } = data;
			patchOptimistic(entryId, { phase: "finalizing" });
			// Hold the "Finalizing…" beat for a consistent ~1s, then refetch and
			// swap the skeleton for the real row together (no flash, no duplicate).
			schedule(() => {
				void loadRecords().finally(() => removeOptimistic(entryId));
			}, OPTIMISTIC_FINALIZE_MS);
		});

		// Deliberately dropped (e.g. non-billable email). Surface it, then clear.
		socket.on("entry_discarded", (data: EntryTerminalEvent) => {
			if (!data?.entryId) return;
			if (data.reason === "non_billable") {
				toast.message("Entry not billable", {
					description: "Hourglass reviewed this activity and skipped it.",
					duration: 5000,
				});
			}
			removeOptimistic(data.entryId);
		});

		// Not turned into an entry at all (deleted today / already exported). These
		// fire before "received", so there's usually no skeleton — just inform.
		socket.on("entry_ignored", (data: EntryTerminalEvent) => {
			if (!data?.entryId) return;
			if (data.reason === "already_exported") {
				toast.message("Entry already exported", {
					description: "This activity maps to an already-exported entry.",
					duration: 5000,
				});
			}
			removeOptimistic(data.entryId);
		});

		// Pipeline threw — resolve the skeleton with an error instead of spinning.
		socket.on("entry_failed", (data: EntryTerminalEvent) => {
			if (!data?.entryId) return;
			patchOptimistic(data.entryId, { phase: "error" });
			toast.error("Entry generation failed", {
				description: "We couldn't generate this entry. Please try again.",
				duration: TOAST_DURATION.error,
			});
			// Give the errored row a moment to show, then clear it.
			schedule(() => removeOptimistic(data.entryId), 4000);
		});

		// Listen for admin feedback updates on attorney entries (still broadcast).
		socket.on("entry_feedback_updated", (data: EntryFeedbackEvent) => {
			if (data.email === userRef.current?.email) {
				loadRecords();
			}
		});

		socket.on("connect_error", (error) => {
			logger.captureException(error, { action: "websocketConnect" });
			if (!showedOfflineToast.current) {
				showedOfflineToast.current = true;
				toast.loading("Reconnecting to Hourglass…", {
					id: "ws-offline",
					duration: Infinity,
					description:
						"Your saved entries are safe. We'll refresh as soon as we're back online.",
				});
			}
		});

		socket.io.on("reconnect_failed", () => {
			showedOfflineToast.current = false;
			toast.dismiss("ws-offline");
			toast.error("Connection error", {
				id: "ws-failed",
				duration: Infinity,
				description: "We can't reach the server right now.",
				action: {
					label: "Refresh and try again",
					onClick: () => window.location.reload(),
				},
			});
		});

		return () => {
			for (const id of timers) clearTimeout(id);
			socket.off("connect");
			socket.off("entry_received");
			socket.off("entry_matter_predicted");
			socket.off("entry_processing_completed");
			socket.off("entry_discarded");
			socket.off("entry_ignored");
			socket.off("entry_failed");
			socket.off("entry_feedback_updated");
			socket.off("connect_error");
			socket.io.off("reconnect_failed");
			socket.disconnect();
		};
	}, [upsertOptimistic, patchOptimistic, removeOptimistic]);

	const value = useMemo<EntriesContextType>(
		() => ({
			isLoading: isAuthLoading,
			optimisticEntries,
			refreshRecords,
		}),
		[isAuthLoading, optimisticEntries, refreshRecords],
	);

	return (
		<EntriesContext.Provider value={value}>{children}</EntriesContext.Provider>
	);
}

export function useEntries() {
	const context = useContext(EntriesContext);
	if (context === undefined) {
		throw new Error("useEntries must be used within an EntriesProvider");
	}
	return context;
}

/**
 * Non-throwing variant — returns `undefined` outside an `EntriesProvider`.
 * The shared `EntriesTable` is also rendered on the admin and internal surfaces,
 * which have no provider (and no processing socket), so components living inside
 * it (e.g. the processing skeleton band) use this to no-op there.
 */
export function useOptionalEntries() {
	return useContext(EntriesContext);
}
